import { useEffect, useRef } from 'react'
import { useAuth } from '@/shared/lib/auth/auth-context'
import { router } from '@/app/routes/routes'
import { appInsights } from '@/shared/lib/analytics/app-insights'

type RouterState = Parameters<Parameters<typeof router.subscribe>[0]>[0]

interface AnalyticsProviderProps {
  children: React.ReactNode
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const { user } = useAuth()
  const userRef = useRef(user)
  // Initialize with the current path to avoid a duplicate page view on mount
  const prevPathname = useRef<string>(router.state.location.pathname)

  // Keep userRef up to date without recreating the route subscriber
  useEffect(() => {
    userRef.current = user
  }, [user])

  // Authenticated user identity
  useEffect(() => {
    if (user) {
      appInsights.setAuthenticatedUserContext(user.userId, String(user.organizationId), true)
    } else {
      appInsights.clearAuthenticatedUserContext()
    }
  }, [user])

  // Page view tracking — registered once, uses userRef for the up-to-date identity
  useEffect(() => {
    return router.subscribe((state: RouterState) => {
      const { pathname, search } = state.location
      if (pathname === prevPathname.current) return
      prevPathname.current = pathname

      const currentUser = userRef.current
      appInsights.trackPageView({
        name: pathname,
        uri: pathname + search,
        isLoggedIn: currentUser !== null,
        properties: {
          tenantId: currentUser ? String(currentUser.organizationId) : undefined,
        },
      })
    })
  }, [])

  return <>{children}</>
}
